import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"
import DOMPurify from "dompurify"

// Renders an assistant message's markdown, live while it streams.
//
// The raw text lives in the hidden `source` target (the element streamed
// chunks are appended into by Message#broadcast_append_chunk); this
// controller re-renders it into `output` at most once per frame. The final
// server render uses the same structure, so the formatting doesn't change
// when the reply finishes streaming.
//
// In a review chat (activityRefs value) every "#123" token — how the review
// prompt tells the model to cite time activities — becomes an icon chip
// linking to that activity's row on the review page behind the drawer
// (`review_time_activity_<id>`). The icon comes from the
// <template data-chat-link-icon="activity"> the chat drawer renders.

// "#123" as a standalone token; same rule the server-side linkifier used.
const ACTIVITY_REF = /(?<![\w&])#(\d+)\b/g

export default class extends Controller {
  static targets = ["source", "output"]
  static values = { activityRefs: Boolean }

  connect() {
    this.render()
    this.observer = new MutationObserver(() => this.scheduleRender())
    this.observer.observe(this.sourceTarget, { childList: true, characterData: true, subtree: true })
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.frame) cancelAnimationFrame(this.frame)
  }

  scheduleRender() {
    if (this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.render()
    })
  }

  render() {
    const html = DOMPurify.sanitize(marked.parse(this.markdown, { gfm: true, breaks: true }))
    const fragment = document.createRange().createContextualFragment(html)
    this.decorateLinks(fragment)
    if (this.activityRefsValue) this.chipActivityRefs(fragment)
    this.outputTarget.replaceChildren(fragment)
  }

  // The source holds either the saved message text (data-markdown-final),
  // or streamed chunks as <span data-chunk="N">, which can arrive out of
  // order; join those by sequence number.
  get markdown() {
    const source = this.sourceTarget
    const chunks = [...source.querySelectorAll(":scope > span[data-chunk]")]
    const saved = [...source.childNodes].filter((node) => !chunks.includes(node)).map((node) => node.textContent).join("")
    if ("markdownFinal" in source.dataset || chunks.length === 0) return saved

    chunks.sort((a, b) => Number(a.dataset.chunk) - Number(b.dataset.chunk))
    return saved + chunks.map((span) => span.textContent).join("")
  }

  // External links open in a new tab so the drawer (and the review page
  // behind it) stays put.
  decorateLinks(root) {
    root.querySelectorAll("a[href]").forEach((a) => {
      if (isExternal(a.getAttribute("href"))) {
        a.target = "_blank"
        a.rel = "noopener noreferrer"
      }
    })
  }

  chipActivityRefs(root) {
    // Bare refs the model put in backticks, e.g. `#123`.
    root.querySelectorAll("code").forEach((code) => {
      if (code.closest("pre")) return
      const match = code.textContent.trim().match(/^#(\d+)$/)
      if (match) code.replaceWith(chip(match[1]))
    })

    // Refs in running text. Collect first: replacing while walking would
    // confuse the walker.
    const walker = document.createTreeWalker(root, NodeFilter.FILTER_SHOW_TEXT, {
      acceptNode: (node) =>
        node.parentElement?.closest("a, code, pre") ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
    })
    const nodes = []
    while (walker.nextNode()) {
      if (ACTIVITY_REF.test(walker.currentNode.nodeValue)) nodes.push(walker.currentNode)
      ACTIVITY_REF.lastIndex = 0
    }

    nodes.forEach((node) => {
      const text = node.nodeValue
      const parts = document.createDocumentFragment()
      let last = 0
      for (const match of text.matchAll(ACTIVITY_REF)) {
        parts.append(text.slice(last, match.index), chip(match[1]))
        last = match.index + match[0].length
      }
      parts.append(text.slice(last))
      node.replaceWith(parts)
    })
  }
}

// True for links that leave the app (other origins, mailto:, etc.).
function isExternal(href) {
  try {
    return new URL(href, window.location.href).origin !== window.location.origin
  } catch {
    return false
  }
}

function chip(id) {
  const a = document.createElement("a")
  a.href = `#review_time_activity_${id}`
  a.className = "chat-chip"
  a.dataset.kind = "activity"
  a.title = `Show time entry #${id} on the review page`

  const icon = document.querySelector('template[data-chat-link-icon="activity"]')
  if (icon) a.append(icon.content.cloneNode(true))

  const text = document.createElement("span")
  text.textContent = `#${id}`
  a.append(text)
  return a
}
